import { apiGet, apiGetOptional, apiPatch, apiPost } from '@/api/http';

import type { FeedbackFormValues } from '@/components/form/FeedbackForm';
import type { AppNavigate } from '@/hooks/useUrlState';
import type {
  InterviewRoom,
  InterviewStage,
  ProposedDate,
  ProposedDatesResponse,
  RejectionFeedbackResponse,
} from '@/types/interview';

const url = import.meta.env.VITE_API_URL;

export type InterviewTaskUpdatePayload = {
  document?: string;
  [key: string]: unknown;
};

export const getInterview = (interview_id: string, navigate?: AppNavigate) =>
  apiGetOptional<InterviewRoom>(
    `${url}interviews/${interview_id}/`,
    undefined,
    navigate,
  );

export const completeTask = (interview_id: string, task_id: string) =>
  apiPost(`${url}interviews/${interview_id}/tasks/${task_id}/complete/`);

export const updateTask = (
  interview_id: string,
  task_id: string,
  data: InterviewTaskUpdatePayload | FormData,
) => apiPatch(`${url}interviews/${interview_id}/tasks/${task_id}/`, data);

export const moveToNextTask = (
  interview_id: string,
  stage_name: string,
  next_stage_id?: string,
  reason_for_change?: string,
) => {
  const data: Record<string, string> = {};
  if (next_stage_id) {
    data.stage_id = next_stage_id;
  } else {
    data.stage_name = stage_name;
  }
  if (reason_for_change) {
    data.reason_for_change = reason_for_change;
  }
  return apiPost(`${url}interviews/${interview_id}/next-stage/`, data);
};

export const getProposedDates = (interview_id: string) =>
  apiGetOptional<ProposedDatesResponse>(
    `${url}interviews/${interview_id}/proposed-dates/`,
  );

export type ProposedDateInput = Partial<ProposedDate> & Record<string, unknown>;

export const proposeInterviewDates = (
  interview_id: string,
  proposed_dates: ProposedDate[] | ProposedDateInput[],
) =>
  apiPost(`${url}interviews/${interview_id}/proposed-dates/`, {
    proposed_dates,
  });

export const confirmProposedDate = (interview_id: string, date_id: string) =>
  apiPost(
    `${url}interviews/${interview_id}/proposed-dates/${date_id}/confirm/`,
  );

export const updateInterviewDates = (
  interview_id: string,
  proposed_date: ProposedDate | Record<string, unknown>,
) => apiPost(`${url}interviews/${interview_id}/planning-dates/`, proposed_date);

export const getClientInterviewStages = () =>
  apiGet<InterviewStage[]>(`${url}interviews/stages/`);

export const sendFeedback = (
  interview_id: string,
  data: Record<string, unknown> | FormData,
) => apiPost(`${url}interviews/${interview_id}/feedbacks/`, data);

export const getFeedback = (interview_id: string, feedback_id: string) =>
  apiGetOptional<Partial<FeedbackFormValues>>(
    `${url}interviews/${interview_id}/feedbacks/${feedback_id}/`,
  );

export const getRejectedFeedback = (interview_id: string) =>
  apiGetOptional<RejectionFeedbackResponse>(
    `${url}interviews/${interview_id}/rejection-feedback/`,
  );

export const atsDisqualifyInterview = (interview_id: string, reason: string) =>
  apiPost(`${url}interviews/${interview_id}/ats-disqualify/`, { reason });

export const sendOffer = (
  interview_id: string,
  data: Record<string, unknown> | FormData,
) => apiPost(`${url}interviews/${interview_id}/offer/`, data);

export const confirmOffer = (interview_id: string) =>
  apiPost(`${url}interviews/${interview_id}/offer/confirm/`);

export const rejectOffer = (
  interview_id: string,
  data: RejectionFeedbackResponse | FormData,
) => apiPost(`${url}interviews/${interview_id}/offer/reject/`, data);

export const disagreeOffer = (
  interview_id: string,
  data: Record<string, unknown> | FormData,
) => apiPost(`${url}interviews/${interview_id}/offer/disagree/`, data);

export const rateInterview = (
  interview_id: string,
  data: Record<string, unknown> | FormData,
) => apiPost(`${url}interviews/${interview_id}/ratings/`, data);

/**
 * Recruiter payload from embed-chat-bootstrap (RecruiterShortSerializer).
 * Nested `company` — not flattened Contact fields.
 */
export type EmbedChatBootstrapRecruiter = {
  id: string;
  name: string;
  image?: string | null;
  recruiter_type?: string;
  company?: {
    id: string;
    name: string;
  } | null;
};

/** Embed bootstrap `contact` is recruiter shape (nested company), not Contact. */
export type EmbedChatBootstrapResponse = {
  interview_id: string;
  contact: EmbedChatBootstrapRecruiter;
  job_id?: string | null;
};

export const getEmbedChatBootstrap = (params: {
  ats_id: string;
  job_id?: string;
  application_id?: string;
  candidate_id?: string;
}) =>
  apiGet<EmbedChatBootstrapResponse>(`${url}interviews/embed-chat-bootstrap/`, {
    params,
  });
